import React from 'react';
import Select from 'react-select';

/**
 * AppSelect — react-select stilizzato col Design System (token), coerente con
 * il componente <Input> (bordo, radius 6px, focus ring teal, opzioni teal).
 * Il menu è renderizzato in un portal su <body> così non viene mai ritagliato
 * dagli overflow/stacking delle card.
 *
 * Passa qualsiasi prop di react-select; `styles` extra vengono mergiate.
 */

const appSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--font-size-body)',
    backgroundColor: state.isDisabled ? 'var(--color-surface-dim)' : 'var(--color-surface)',
    borderColor: state.isFocused ? 'var(--color-teal)' : 'var(--color-input-border)',
    borderRadius: 'var(--radius-sm)',
    boxShadow: state.isFocused ? '0 0 0 3px var(--color-teal-container)' : 'none',
    transition: 'border-color .15s ease, box-shadow .15s ease',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--color-teal)' : 'var(--color-primary-border)',
    },
  }),
  valueContainer: (base) => ({ ...base, padding: '2px 10px' }),
  placeholder: (base) => ({ ...base, color: 'var(--color-on-surface-muted)' }),
  singleValue: (base) => ({ ...base, color: 'var(--color-on-surface-strong)' }),
  input: (base) => ({ ...base, color: 'var(--color-on-surface-strong)' }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: 'var(--color-input-border)' }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? 'var(--color-teal)' : 'var(--color-on-surface-muted)',
    '&:hover': { color: 'var(--color-teal)' },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'var(--color-on-surface-muted)',
    '&:hover': { color: 'var(--color-danger)' },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-3)',
    border: '1px solid var(--color-surface-border)',
    overflow: 'hidden',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 20000 }),
  option: (base, state) => ({
    ...base,
    cursor: 'pointer',
    fontSize: 'var(--font-size-label-md)',
    color: state.isSelected ? 'var(--color-on-teal)' : 'var(--color-on-surface-strong)',
    backgroundColor: state.isSelected
      ? 'var(--color-teal)'
      : state.isFocused
        ? 'var(--color-teal-container)'
        : 'transparent',
    ':active': { backgroundColor: 'var(--color-teal-container)' },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'var(--color-teal-container)',
    borderRadius: 'var(--radius-sm)',
  }),
  multiValueLabel: (base) => ({ ...base, color: 'var(--color-teal-hover)' }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'var(--color-teal-hover)',
    ':hover': { backgroundColor: 'var(--color-teal)', color: 'var(--color-on-teal)' },
  }),
};

export default function AppSelect({ styles, ...props }) {
  return (
    <Select
      classNamePrefix="app-select"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      styles={{ ...appSelectStyles, ...styles }}
      {...props}
    />
  );
}
